import React, { useEffect, useState } from 'react';
import { FormControl, FormGroup, FormLabel } from 'react-bootstrap';
import { useTheme } from 'styled-components';

interface CustomInputTextProps {
  id?: string;
  name: string;
  title: string;
  value?: string;
  readOnly?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  required?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  type?: 'text' | 'textArea';
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  style?: React.CSSProperties;
  'data-testid'?: boolean | string;
}

const CustomInputText: React.FC<CustomInputTextProps> = ({
  id,
  name,
  title,
  value = '',
  readOnly = false,
  onChange,
  required = false,
  textAlign = 'left',
  type = 'text',
  placeholder,
  rows,
  maxLength,
  style,
  'data-testid': dataTestId,
}) => {
  const [internalValue, setInternalValue] = useState(value);
  const theme = useTheme();

  const controlId = id ?? name;

  const testId =
    typeof dataTestId === 'string'
      ? dataTestId
      : dataTestId === true
        ? `${type === 'textArea' ? 'input-textarea' : 'input-text'}-${controlId}`
        : undefined;

  useEffect(() => {
    setInternalValue(value ?? '');
  }, [value]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (!readOnly) {
      setInternalValue(event.target.value);
    }
    onChange?.(event);
  };

  const commonProps = {
    id: id,
    name: name,
    value: internalValue,
    onChange: handleChange,
    readOnly: readOnly,
    required: required,
    maxLength: maxLength,
    placeholder: placeholder,
    'data-testid': testId,
    style: {
      borderRadius: '10px',
      border: `1px solid ${theme.colors.bordaInput}`,
      textAlign: textAlign,
      backgroundColor: readOnly ? '#e9ecef' : theme.colors.white,
      ...(style || {}),
    },
  };

  const inputStyle: React.CSSProperties = {
    borderRadius: '10px',
    border: `1px solid ${theme.colors.bordaInput}`,
    textAlign,
    backgroundColor: readOnly ? '#e9ecef' : theme.colors.white,
    ...style, // 👈 sobrescrita final
  };

  return (
    <FormGroup>
      {title && (
        <FormLabel
          style={{ color: theme.colors.botaoConfirmar }}
          data-testid={testId ? `${testId}-label` : undefined}
        >
          {required ? (
            <>
              <span
                style={{
                  color: theme.colors.botaoConfirmar,
                  fontWeight: 'bold',
                }}
              >
                {title}
              </span>
              <span
                style={{ color: theme.colors.vermelho, fontWeight: 'bold' }}
              >
                *
              </span>
            </>
          ) : (
            title
          )}
        </FormLabel>
      )}
      {type === 'textArea' ? (
        <FormControl
          {...commonProps}
          as="textarea"
          rows={rows}
          style={inputStyle}
        />
      ) : (
        <FormControl
          {...commonProps}
          as="input"
          type="text"
          style={inputStyle}
        />
      )}
    </FormGroup>
  );
};

export default CustomInputText;
